// src/pages/appointment-calendar/components/WorkingHoursDrawer.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";

const HHMM = (h, m = 0) =>
  `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

function isHHMM(value) {
  return /^\d{2}:\d{2}$/.test(value);
}

function getWeekdays(t) {
  return [
    { k: "0", label: t("staff.dayNames.monday") },
    { k: "1", label: t("staff.dayNames.tuesday") },
    { k: "2", label: t("staff.dayNames.wednesday") },
    { k: "3", label: t("staff.dayNames.thursday") },
    { k: "4", label: t("staff.dayNames.friday") },
    { k: "5", label: t("staff.dayNames.saturday") },
    { k: "6", label: t("staff.dayNames.sunday") },
  ];
}

function toInitialState(weekdays) {
  return {
    timezone: "Europe/Bucharest",
    recurring: weekdays.reduce((acc, d) => {
      acc[d.k] = [];
      return acc;
    }, {}),
  };
}

function normalizeRecurring(rec, weekdays) {
  const base = toInitialState(weekdays).recurring;
  const out = { ...base };
  if (!rec || typeof rec !== "object") return out;

  for (const d of weekdays) {
    const arr = Array.isArray(rec[d.k]) ? rec[d.k] : [];
    const first = arr[0];
    if (first && typeof first === "object" && first.start && first.end) {
      out[d.k] = [{ start: first.start, end: first.end }];
    } else {
      out[d.k] = [];
    }
  }
  return out;
}

export default function WorkingHoursDrawer({ locationId, onClose }) {
  const { t } = useTranslation();
  const weekdays = useMemo(() => getWeekdays(t), [t]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [state, setState] = useState(() => toInitialState(weekdays));

  const businessId = useMemo(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem("current_business_id");
  }, []);

  // Load current working hours
  useEffect(() => {
    if (!locationId || !businessId) return;
    let abort = false;

    async function run() {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get("/b/working-hours", {
          params: { location_id: locationId },
          headers: { "X-Business-Id": businessId },
        });
        if (!abort) {
          setState({
            timezone: data?.timezone || "Europe/Bucharest",
            recurring: normalizeRecurring(data?.recurring, weekdays),
          });
        }
      } catch (e) {
        if (!abort) {
          console.error("Eroare la încărcarea programului:", e?.response?.data || e);
          setError(
            e?.response?.data?.detail || t("calendar.loadWorkingHoursError")
          );
        }
      } finally {
        if (!abort) setLoading(false);
      }
    }

    run();
    return () => {
      abort = true;
    };
  }, [locationId, businessId]);

  async function handleSave() {
    if (!locationId || !businessId) return;
    setSaving(true);
    setError("");
    try {
      await api.put(
        "/b/working-hours",
        {
          location_id: locationId,
          timezone: state.timezone,
          recurring: state.recurring,
        },
        {
          headers: { "X-Business-Id": businessId },
        }
      );

      // 🔔 anunțăm aplicația că s-a schimbat programul de lucru
      try {
        window.dispatchEvent(
          new CustomEvent("working-hours:updated", {
            detail: { locationId },
          })
        );
      } catch {
        // dacă nu există window (teste / SSR), ignorăm
      }

      onClose?.();
    } catch (e) {
      console.error("Eroare la salvare program:", e?.response?.data || e);
      setError(
        e?.response?.data?.detail ||
          e?.response?.data?.errors ||
          t("calendar.saveWorkingHoursError")
      );
    } finally {
      setSaving(false);
    }
  }

  function toggleClosed(dayKey, closed) {
    setState((prev) => {
      const copy = { ...prev, recurring: { ...prev.recurring } };
      copy.recurring[dayKey] = closed
        ? []
        : [{ start: HHMM(9, 0), end: HHMM(17, 0) }];
      return copy;
    });
  }

  function setTime(dayKey, which, value) {
    if (!isHHMM(value)) return;
    setState((prev) => {
      const copy = { ...prev, recurring: { ...prev.recurring } };
      const arr = copy.recurring[dayKey] || [];
      if (arr.length === 0) {
        copy.recurring[dayKey] = [{ start: HHMM(9, 0), end: HHMM(17, 0) }];
      }
      copy.recurring[dayKey][0] = {
        ...copy.recurring[dayKey][0],
        [which]: value,
      };
      return copy;
    });
  }

  if (!locationId) return null;

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40 backdrop-blur-sm mt-16">
      <div className="h-full w-full max-w-md bg-surface text-foreground shadow-xl flex flex-col">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-sm font-semibold">
            {t("calendar.workingHoursLocation", { id: locationId })}
          </h2>
          <button
            type="button"
            className="text-sm text-gray-500 hover:text-gray-800"
            onClick={onClose}
          >
            {t("common.close")}
          </button>
        </div>

        <div className="flex-1 overflow-auto px-4 py-3 text-sm">
          {loading && <p>{t("calendar.loadingWorkingHours")}</p>}
          {error && (
            <p className="mb-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
              {error}
            </p>
          )}

          {!loading && !error && (
            <>
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t("calendar.timezone")}
                </label>
                <input
                  type="text"
                  className="w-full rounded border px-2 py-1 text-sm"
                  value={state.timezone}
                  onChange={(e) =>
                    setState((prev) => ({
                      ...prev,
                      timezone: e.target.value || "Europe/Bucharest",
                    }))
                  }
                />
              </div>

              <div className="space-y-3">
                {weekdays.map((d) => {
                  const arr = state.recurring[d.k] || [];
                  const closed = arr.length === 0;
                  const interval =
                    arr[0] || { start: HHMM(9, 0), end: HHMM(17, 0) };
                  return (
                    <div
                      key={d.k}
                      className="flex items-center justify-between gap-2"
                    >
                      <div className="w-24 text-sm">{d.label}</div>
                      <label className="flex items-center gap-1 text-xs">
                        <input
                          type="checkbox"
                          checked={closed}
                          onChange={(e) =>
                            toggleClosed(d.k, e.target.checked)
                          }
                        />
                        <span>{t("calendar.closed")}</span>
                      </label>
                      {!closed && (
                        <div className="flex items-center gap-2">
                          <input
                            type="time"
                            className="rounded border px-1 py-0.5 text-xs"
                            value={interval.start}
                            onChange={(e) =>
                              setTime(d.k, "start", e.target.value)
                            }
                          />
                          <span className="text-xs">–</span>
                          <input
                            type="time"
                            className="rounded border px-1 py-0.5 text-xs"
                            value={interval.end}
                            onChange={(e) =>
                              setTime(d.k, "end", e.target.value)
                            }
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="border-t px-4 py-3 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border px-3 py-1 text-sm"
            onClick={onClose}
            disabled={saving}
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
            onClick={handleSave}
            disabled={saving || !businessId}
          >
            {saving ? t("calendar.savingWorkingHours") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
